import { Box, Stack, Typography, useTheme } from "@mui/material";
import { DataTable, SelectField } from "components";
import {
  useGetAllCustomers,
  useGetDattoCustomers,
  useGetDattoMappedCustomers,
  useMapDattoCustomers,
} from "controller";
import { useSetAtom } from "jotai";
import { isDattoMappingListLoadingAtom } from "pages/modules/control-center/integrations/store/IntegrationStore";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import toast from "react-hot-toast";
import { useDebounce } from "utilz/helpers/hooks/useDebounceHook";

// import { useInView } from "react-intersection-observer";

const TableHeaders = [
  {
    name: "Datto site",
    sortable: false,
    key: "external_customer_name",
  },
  {
    name: "DeskDay customer",
    sortable: false,
    key: "ddCustomer",
  },
];

/**
 * Skeleton rows shown while a list is loading. DataTable renders
 * `[...Array(itemsPerPage)]` placeholders, so passing 0 here makes
 * `isDataLoading` render nothing at all — the table just looks empty.
 */
const LOADING_SKELETON_ROWS = 8;

export const DattoCustomerMapping = forwardRef(
  ({ onStepChange }: { onStepChange: (step: number) => void }, ref) => {
    const theme = useTheme();
    const { mutateAsync: mapCustomer } = useMapDattoCustomers();
    // Debounced server-side search — keeps customers beyond the first page
    // reachable instead of filtering only the loaded page.
    const [customerSearch, setCustomerSearch] = useDebounce("", 400);
    const { data: customers, isFetching: isCustomersFetching } =
      useGetAllCustomers({
        pageNumber: 1,
        perPageCount: 2000,
        sortColumn: "customer_name",
        sortDirection: "asc",
        filter: [{ column: "is_active", operator: "=", value: "true" }],
        searchQuery: customerSearch,
      });
    const {
      data: dattoCustomersData,
      isLoading: isDattoCustomersLoading,
      fetchNextPage: fetchDattoCustomersNextPage,
      isFetchingNextPage: isFetchingDattoCustomersNextPage,
      hasNextPage,
    } = useGetDattoCustomers();

    // Every unmapped site has to be reachable here — a site left off the list can
    // never be mapped, and none of its devices can be mapped in the next step
    // either. `totalCount` is not surfaced anywhere, so a truncated list is
    // indistinguishable from a complete one; pull the pages instead of asking the
    // user to click through them blind.
    useEffect(() => {
      if (hasNextPage && !isFetchingDattoCustomersNextPage) {
        fetchDattoCustomersNextPage();
      }
    }, [
      hasNextPage,
      isFetchingDattoCustomersNextPage,
      fetchDattoCustomersNextPage,
    ]);

    // True for the WHOLE walk, not just page 1, so the table shows its loader
    // instead of asserting "No data found" while later pages are still landing.
    const isSiteListLoading =
      isDattoCustomersLoading ||
      isFetchingDattoCustomersNextPage ||
      Boolean(hasNextPage);

    // The step's Next button lives in the parent screen, so publish the walk
    // state for it to disable against. Cleared on unmount so leaving the step
    // never strands the button in a disabled state.
    const setIsMappingListLoading = useSetAtom(isDattoMappingListLoadingAtom);
    useEffect(() => {
      setIsMappingListLoading(isSiteListLoading);
      return () => setIsMappingListLoading(false);
    }, [isSiteListLoading, setIsMappingListLoading]);

    // const { ref: loadMoreRef, inView } = useInView({
    //   threshold: 0.1,
    //   triggerOnce: false,
    //   onChange: (inView) => {
    //     console.log("In view:", inView);
    //     if (inView && !isFetchingDattoCustomersNextPage) {
    //       console.log("Fetching next page...");
    //       // if (dattoCustomersData?.pages?.[-1]?.data?.length > 0) {
    //       fetchDattoCustomersNextPage();
    //       // }
    //     }
    //   },
    // });

    // useEffect(() => {
    //   if (inView && !isFetchingDattoCustomersNextPage) {
    //     fetchDattoCustomersNextPage();
    //   }
    // }, [inView, isFetchingDattoCustomersNextPage, fetchDattoCustomersNextPage]);

    const {
      data: dattoMappedCustomers,
      isLoading: isDattoMappedCustomersLoading,
    } = useGetDattoMappedCustomers();
    const [transformedCustomerData, setTransformedCustomerData] = useState<
      any[]
    >([]);

    // The rows are built in the effects below, a render after the queries
    // settle. Keep the skeleton up until they exist.
    const isBuildingRows =
      Boolean(dattoCustomersData?.pages?.some((page) => page?.data?.length)) &&
      Boolean(dattoMappedCustomers) &&
      transformedCustomerData.length === 0;
    const [allDattoCustomers, setAllDattoCustomers] = useState<any[]>([]);

    useEffect(() => {
      if (dattoCustomersData?.pages) {
        const allCustomers =
          dattoCustomersData.pages.flatMap((page: any) => page?.data || []) ||
          [];
        setAllDattoCustomers(allCustomers);
      }
    }, [dattoCustomersData]);

    useEffect(() => {
      if (allDattoCustomers.length > 0 && dattoMappedCustomers) {
        createMappingArray();
      }
    }, [allDattoCustomers, dattoMappedCustomers]);

    const createMappingArray = () => {
      const customerArray = allDattoCustomers?.map(
        (customer: Record<string, any>) => {
          // The mapping endpoint keeps returning soft-deleted rows, so without
          // the `is_deleted` check an unmapped site still renders as mapped to
          // its old DeskDay customer.
          const customerIndex = dattoMappedCustomers?.findIndex(
            (mappedCustomer: Record<string, any>) =>
              String(mappedCustomer.external_customer_id) ===
                String(customer.id) && mappedCustomer.is_deleted === false,
          );
          const customerObject = {
            deskday_customer_id:
              customerIndex > -1
                ? dattoMappedCustomers[customerIndex].deskday_customer_id
                : "",
            deskday_customer_name:
              customerIndex > -1
                ? dattoMappedCustomers[customerIndex].deskday_customer_name
                : "",
            external_customer_id: customer.id,
            external_customer_name: customer.name,
          };
          return customerObject;
        },
      );
      setTransformedCustomerData(customerArray || []);
    };

    const addCustomerMapping = ({
      rowData,
      customerId,
      rowIndex,
    }: {
      rowData: any;
      customerId: string | null;
      rowIndex: number;
    }) => {
      if (!customerId) {
        setTransformedCustomerData((prev) => {
          const newPrev = [...prev];
          newPrev[rowIndex].deskday_customer_id = customerId;
          newPrev[rowIndex].deskday_customer_name = null;
          return newPrev;
        });
        return;
      }
      const customerIndex = customers?.result.findIndex(
        (customer: Record<string, any>) => customer.mapping_id === customerId,
      );
      if (customerIndex !== -1) {
        setTransformedCustomerData((prev) => {
          const newPrev = [...prev];
          newPrev[rowIndex].deskday_customer_id = customerId;
          newPrev[rowIndex].deskday_customer_name =
            customers?.result[customerIndex].customer_name;
          return newPrev;
        });
      }
    };

    const cellBuilders = {
      ddCustomer: (rowData: any, index?: number) => {
        return (
          <SelectField
            sx={{ width: "180px" }}
            name="customer"
            hideHelperBox
            options={customers?.result || []}
            isSearchable
            isLoading={isCustomersFetching}
            onSearchValueChange={setCustomerSearch}
            // Search is served by the backend (`searchQuery`), so `onFilterValue`
            // returns true to stop the client from re-filtering the results.
            onFilterValue={() => true}
            value={rowData.deskday_customer_id}
            size="small"
            isResponsive={false}
            labelMappingKey="customer_name"
            valueMappingKey="mapping_id"
            // The options list shrinks while a search is active, so fall back to
            // the name held in row state to keep every row's label stable.
            renderValue={(_value, optionValue) =>
              optionValue?.customer_name || rowData.deskday_customer_name || ""
            }
            searchFieldProps={{
              placeholder: "Search customer",
            }}
            onValueChange={(value) =>
              addCustomerMapping({
                rowData,
                customerId: value,
                rowIndex: index || 0,
              })
            }
            isClearable
          />
        );
      },
    };

    const handleTransformForSubmit = () => {
      const submitArray = transformedCustomerData
        .filter((customer) => customer.deskday_customer_id)
        .map((customer) => ({
          external_customer_id: customer.external_customer_id,
          external_customer_name: customer.external_customer_name,
          deskday_customer_id: customer.deskday_customer_id,
          deskday_customer_name: customer.deskday_customer_name,
        }));
      return submitArray;
    };

    const onSubmit = async () => {
      // Same reasoning as the device step: the payload only carries sites already
      // loaded, so saving mid-walk risks dropping mappings for sites still on an
      // unfetched page.
      // Backstop only — the Next button is already disabled while this is true.
      // The "Save your changes?" modal reaches onSubmit by another path, so the
      // check stays here too.
      if (isSiteListLoading) {
        toast.error("Sites are still loading. Please try again in a moment.");
        return false;
      }
      try {
        const mappedCustomers = handleTransformForSubmit();
        await mapCustomer(mappedCustomers);
        mappedCustomers?.length > 0 && toast.success("Customer mapping saved");
        return true;
      } catch (error: any) {
        toast.error("Failed to map customers.");
        return false;
      }
    };

    useImperativeHandle(ref, () => ({
      onSubmit,
    }));

    return (
      <Stack
        sx={{
          px: "32px",
          pt: "24px",
          pb: "0px",
          gap: "16px",
          height: "100%",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6">Customer mapping</Typography>
        <Typography variant="body-medium" color={theme.palette.lightBg.low}>
          Link Datto sites with DeskDay customers to align alerts. You can
          assign mapped site devices to users in the next step.
        </Typography>
        <Box
          sx={{
            flex: 1,
            width: "100%",
            height: "100%",
            overflow: "hidden",
            display: "flex",
          }}
        >
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <DataTable
              isDataLoading={
                isSiteListLoading ||
                isDattoMappedCustomersLoading ||
                isBuildingRows
              }
              headers={TableHeaders || []}
              data={transformedCustomerData || []}
              uniqueKey="external_customer_id"
              cellBuilders={cellBuilders}
              selectedItems={[]}
              currentPageNumber={0}
              itemsPerPage={LOADING_SKELETON_ROWS}
              multiselect={false}
              containerStyles={{
                height: "100%",
                minHeight: "200px",
                overflow: "auto",
                paddingBottom: "15px",
              }}
              // containerStyles={{
              //   height: {
              //     xs: "calc(100vh - 310px)",
              //     sm: "calc(100vh - 310px)",
              //     md: "calc(100vh - 312px)",
              //     lg: "calc(100vh - 310px)",
              //   },
              //   paddingBottom: "16px",
              // }}
              headerCellStyles={{
                textTransform: "none",
              }}
            />
          </Box>
          {/* Move the loadMoreRef inside the scrollable area */}
          {/* <div
              ref={loadMoreRef}
              style={{
                height: "20px",
                marginTop: "10px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isFetchingDattoCustomersNextPage && (
                <CircularProgress size={24} />
              )}
            </div> */}
        </Box>
      </Stack>
    );
  },
);
